import React, { useContext, useEffect } from 'react';
import styled from 'styled-components';
import {
    PartnerCouponFragment,
    ProductFixedPriceProjectFragment,
} from '@99designs/apps/spa/pages/create/briefs/Brief.generated';
import ContentSetting from '@99designs/apps/spa/pages/create/components/ContentSetting';
import { trackViewedBriefEvent } from '@99designs/apps/spa/pages/create/events';
import { BriefContext, BriefPackage } from '@99designs/brief';
import {
    Box,
    CautionAlert,
    Link,
    Matrix,
    SectionHeading,
    SmallSectionHeading,
    Stack,
} from '@99designs/emu';
import { borders, breakpoints, colors, spaces } from '@99designs/emu/settings';
import { useIsExperimentActivated } from '@99designs/experiments';
import { FormattedReactMessage } from '@99designs/frontend-components/i18n';
import { PartnerCouponValidityState } from '@99designs/graph-utils/types';
import { CurrencyCode } from '@99designs/i18n';
import { linkTo } from '@99designs/routing';
import { Price } from '@99designs/spa-common';
import { PartnerContext } from '@99designs/spa-common';

const InvoicePageWrapper = styled.div`
    display: flex;
    flex-direction: column;

    @media screen and (min-width: ${breakpoints.normal[0]}px) {
        flex-direction: row;
    }
`;

const Item = styled.div`
    flex: ${({ flex }: { flex: number }) => flex};
`;

const Invoice = styled.div`
    padding: ${spaces['2bu']} ${spaces['1bu']};

    @media screen and (min-width: ${breakpoints.normal[0]}px) {
        padding: ${spaces['1bu']} ${spaces['2bu']};
    }
`;

const Total = styled.div`
    border-top: ${borders.content};
    margin-top: ${spaces['1bu']};
    padding-top: ${spaces['2bu']};
`;

function RowSpaceBetween({ children }: { children?: React.ReactNode }) {
    return (
        <Box display="flex" flexDirection="row" justifyContent="space-between">
            {children}
        </Box>
    );
}

function InvalidCouponWarning({ state }: { state: PartnerCouponValidityState }) {
    let msg;
    switch (state) {
        case 'EXPIRED':
            msg = __('It looks like your promotion has expired. Pay the invoice to continue');
            break;
        case 'ALREADY_REDEEMED':
            msg = __('It looks like your promotion has been claimed. Pay the invoice to continue');
            break;
        default:
            msg = __(
                'It looks like there is a problem with your promotion. Please contact support'
            );
            break;
    }

    return <CautionAlert>{msg}</CautionAlert>;
}

export default function InvoicePage({
    brief,
}: {
    brief: {
        id: string;
        product: ProductFixedPriceProjectFragment | null;
        partnerCoupon?: PartnerCouponFragment | null;
        designers: { id: string }[];
    };
}) {
    if (!brief.product) {
        throw new Error('product not found');
    }

    const { color } = useContext(PartnerContext);
    const { paymentSource } = useContext(BriefContext);
    const isFunded = paymentSource === 'fully-funded';
    const hasLocalizedTax = useIsExperimentActivated('localizedTax', 'enabled');

    const howPaymentWorks = () => {
        let content = [
            __('99designs will securely hold your payment once you pay this invoice.'),
            __(
                'Once your designer completes all the work listed, you can confirm and release the funds to them.'
            ),
        ];

        if (isFunded) {
            content = [
                __(
                    '99designs will securely hold your payment once you confirm this funded project.'
                ),
                __(
                    'Once your designer completes all the work listed, you can confirm and release the funds to them.'
                ),
            ];
        }

        return {
            title: __('How payment works'),
            content,
        };
    };

    const whatHappensNext = () => {
        let content = [
            __(
                'Your project will be displayed to a wide range of qualified designers, who can express interest in working with you. You can then invite an interested designer to join your project.'
            ),
            __(
                'Your designer will work closely with you to understand your needs. Collaborate with them to refine their design and create stunning content!'
            ),
        ];

        if (brief.product?.key.includes('full-service-branding')) {
            content = [
                __(
                    "A 99designs Studio Project Manager will reach out immediately to arrange an onboarding call, run through your brief, and discuss any additional services you may need. Your project will be displayed to an exclusive group of Creative Directors, and your Project Manager will ensure you're matched with the most suitable one for your branding project."
                ),
                __(
                    "Your Creative Director will then confirm they're ready to begin working on this project by opting in. You'll accept and continue to work closely together to deliver a unique brand for your business."
                ),
            ];
        }

        return {
            title: __('What happens next'),
            content,
        };
    };

    const faqs = [howPaymentWorks(), whatHappensNext()];

    useEffect(() => {
        trackViewedBriefEvent({ id: brief.id, page: 'Invoice' });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <ContentSetting>
            <InvoicePageWrapper>
                <Item flex={2}>
                    <BriefPackage product={brief.product} />
                </Item>
                <Item flex={4}>
                    <Invoice>
                        <Stack space="3bu">
                            {brief.partnerCoupon && brief.partnerCoupon.state !== 'VALID' && (
                                <Stack space="1bu">
                                    <InvalidCouponWarning state={brief.partnerCoupon.state} />
                                </Stack>
                            )}
                            <Stack space="1bu">
                                <RowSpaceBetween>
                                    <SmallSectionHeading>{__('Description')}</SmallSectionHeading>
                                    <SmallSectionHeading>{__('Amount')}</SmallSectionHeading>
                                </RowSpaceBetween>
                                <RowSpaceBetween>
                                    <span>{brief.product.title}</span>
                                    <Price
                                        amount={brief.product.pricing.subtotal}
                                        currency={
                                            brief.product.pricing.amount.currency as CurrencyCode
                                        }
                                        minimumFractionDigits={2}
                                    />
                                </RowSpaceBetween>
                                <Box height={spaces['1bu']} />
                                <RowSpaceBetween>
                                    <span>{__('Subtotal')}</span>
                                    <Price
                                        amount={brief.product.pricing.subtotal}
                                        currency={
                                            brief.product.pricing.amount.currency as CurrencyCode
                                        }
                                        minimumFractionDigits={2}
                                    />
                                </RowSpaceBetween>
                                <RowSpaceBetween>
                                    <span>{__('Fees')}</span>
                                    <Price
                                        amount={
                                            brief.product.pricing.amount.amountInCents -
                                            brief.product.pricing.subtotal
                                        }
                                        currency={
                                            brief.product.pricing.amount.currency as CurrencyCode
                                        }
                                        minimumFractionDigits={2}
                                    />
                                </RowSpaceBetween>
                                {hasLocalizedTax && (
                                    <RowSpaceBetween>
                                        <span style={{ color: colors.grey500 }}>
                                            {__('Tax calculated at checkout')}
                                        </span>
                                    </RowSpaceBetween>
                                )}
                                {!hasLocalizedTax && brief.product.pricing.gst && !isFunded && (
                                    <RowSpaceBetween>
                                        <span>GST</span>
                                        <Price
                                            amount={brief.product.pricing.gst}
                                            currency={
                                                brief.product.pricing.amount
                                                    .currency as CurrencyCode
                                            }
                                            minimumFractionDigits={2}
                                        />
                                    </RowSpaceBetween>
                                )}
                                <Total>
                                    <RowSpaceBetween>
                                        <SectionHeading>{__('Total')}</SectionHeading>
                                        <SectionHeading>
                                            <Price
                                                amount={
                                                    brief.product.pricing.amount.amountInCents +
                                                    (brief.product.pricing.gst && !isFunded
                                                        ? brief.product.pricing.gst
                                                        : 0)
                                                }
                                                currency={
                                                    brief.product.pricing.amount
                                                        .currency as CurrencyCode
                                                }
                                                minimumFractionDigits={2}
                                                paymentSource={paymentSource}
                                                color={color}
                                            />
                                        </SectionHeading>
                                    </RowSpaceBetween>
                                </Total>
                                <Box>
                                    <FormattedReactMessage
                                        message={__(
                                            'By paying this invoice, you agree to the terms of the <Link>design transfer agreement</Link>.'
                                        )}
                                        replacements={{
                                            Link: <Link href={linkTo.designTransferAgreement()} />,
                                        }}
                                    />
                                </Box>
                            </Stack>
                            <Matrix columns={[1, faqs.length]} space="1bu">
                                {faqs.map((item) => (
                                    <Stack space="1bu" key={item.title}>
                                        <SmallSectionHeading>{item.title}</SmallSectionHeading>
                                        {item.content.map((line) => (
                                            <Box key={line}>{line}</Box>
                                        ))}
                                    </Stack>
                                ))}
                            </Matrix>
                        </Stack>
                    </Invoice>
                </Item>
            </InvoicePageWrapper>
        </ContentSetting>
    );
}
